import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import Layout from './Layout';
import OverallSummary from './OverallSummary';
import ClientSummary from './ClientSummary';
import CourierDispatchReport from './CourierDispatchReport';
import FulfillmentReport from './FulfillmentReport';
import ReportKpiCards from './ReportKpiCards';
import {
  exportCourierDispatchSummary,
  exportFulfillmentReport,
  exportSummary,
  exportSummaryPerClient,
  getClientsName,
  getSummary,
  getSummaryPerClient,
} from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import {
  aggregateReportSummary,
  DATE_RANGE_OPTIONS,
  getPreviousPeriod,
  getReportDateRange,
  readReportWorkspaceState,
  REPORT_FILTER_STORAGE_KEY,
} from '../utils/reportWorkspace';

const REPORTS = [
  {
    value: 'overall',
    label: 'Overall Summary',
    description: 'Volume, dispatch performance and order trends',
    icon: AssessmentOutlinedIcon,
  },
  {
    value: 'client',
    label: 'Client Summary',
    description: 'Compare client volumes and outstanding work',
    icon: GroupsOutlinedIcon,
  },
  {
    value: 'courier',
    label: 'Courier Dispatch',
    description: 'Orders sent by carrier across the selected period',
    icon: LocalShippingOutlinedIcon,
  },
  {
    value: 'fulfillment',
    label: 'Fulfillment Time',
    description: 'Working-day fulfillment distribution by client',
    icon: TimerOutlinedIcon,
  },
];

const DEFAULT_FILTERS = {
  activeReport: 'overall',
  granularity: 'Day',
  dateRangeOption: 'last7days',
  selectedClients: [],
  comparePrevious: true,
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const Report = () => {
  const initialState = useMemo(() => readReportWorkspaceState(), []);
  const [activeReport, setActiveReport] = useState(initialState.activeReport);
  const [granularity, setGranularity] = useState(initialState.granularity);
  const [dateRangeOption, setDateRangeOption] = useState(initialState.dateRangeOption);
  const [startDate, setStartDate] = useState(initialState.startDate);
  const [endDate, setEndDate] = useState(initialState.endDate);
  const [selectedClients, setSelectedClients] = useState(initialState.selectedClients);
  const [comparePrevious, setComparePrevious] = useState(initialState.comparePrevious);
  const [summaryData, setSummaryData] = useState([]);
  const [previousSummaryData, setPreviousSummaryData] = useState([]);
  const [clientSummaryData, setClientSummaryData] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { showSnackbar } = useSnackbar();

  const previousPeriod = useMemo(
    () => getPreviousPeriod(startDate, endDate),
    [startDate, endDate]
  );
  const currentKpis = useMemo(() => aggregateReportSummary(summaryData), [summaryData]);
  const previousKpis = useMemo(
    () => aggregateReportSummary(previousSummaryData),
    [previousSummaryData]
  );
  const availableGranularities = useMemo(() => {
    const days = moment(endDate).diff(moment(startDate), 'days') + 1;
    return days > 90 ? ['Month'] : ['Day', 'Week', 'Month'];
  }, [startDate, endDate]);
  const clientOptions = useMemo(
    () => clients
      .map((client) => client.clientName)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b)),
    [clients]
  );
  const activeReportMeta = REPORTS.find((report) => report.value === activeReport) || REPORTS[0];

  useEffect(() => {
    if (!availableGranularities.includes(granularity)) {
      setGranularity('Month');
    }
  }, [availableGranularities, granularity]);

  useEffect(() => {
    localStorage.setItem(REPORT_FILTER_STORAGE_KEY, JSON.stringify({
      activeReport,
      granularity,
      dateRangeOption,
      startDate,
      endDate,
      selectedClients,
      comparePrevious,
    }));
  }, [
    activeReport,
    comparePrevious,
    dateRangeOption,
    endDate,
    granularity,
    selectedClients,
    startDate,
  ]);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const result = await getClientsName();
        setClients(result.success && Array.isArray(result.clients) ? result.clients : []);
      } catch (error) {
        console.error('Unable to load report clients:', error);
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    if (clientOptions.length === 0 || selectedClients.length === 0) return;
    const validClients = selectedClients.filter((client) => clientOptions.includes(client));
    if (validClients.length !== selectedClients.length) {
      setSelectedClients(validClients);
    }
  }, [clientOptions, selectedClients]);

  const fetchWorkspaceData = useCallback(async () => {
    if (!moment(startDate, 'YYYY-MM-DD', true).isValid()
      || !moment(endDate, 'YYYY-MM-DD', true).isValid()
      || moment(startDate).isAfter(moment(endDate))) {
      setLoadError('Choose a valid date range where the start date is on or before the end date.');
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const currentRequests = [
        getSummary(granularity, startDate, endDate, selectedClients),
        getSummaryPerClient(granularity, startDate, endDate, selectedClients),
      ];
      const previousRequest = comparePrevious && previousPeriod
        ? getSummary(
          granularity,
          previousPeriod.startDate,
          previousPeriod.endDate,
          selectedClients
        )
        : Promise.resolve([]);
      const [currentSummary, perClient, previousSummary] = await Promise.all([
        ...currentRequests,
        previousRequest,
      ]);

      setSummaryData(Array.isArray(currentSummary) ? currentSummary : []);
      setClientSummaryData(Array.isArray(perClient) ? perClient : []);
      setPreviousSummaryData(Array.isArray(previousSummary) ? previousSummary : []);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Fetch management reports error:', error);
      setLoadError(error.response?.data?.message || 'Unable to load report data. Please retry.');
      showSnackbar('Failed to load report data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    comparePrevious,
    endDate,
    granularity,
    previousPeriod,
    selectedClients,
    showSnackbar,
    startDate,
  ]);

  useEffect(() => {
    fetchWorkspaceData();
  }, [fetchWorkspaceData, refreshKey]);

  const handleDateRangeOptionChange = (event) => {
    const option = event.target.value;
    setDateRangeOption(option);
    const range = getReportDateRange(option);
    if (range) {
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  };

  const handleReset = () => {
    const range = getReportDateRange(DEFAULT_FILTERS.dateRangeOption);
    setActiveReport(DEFAULT_FILTERS.activeReport);
    setGranularity(DEFAULT_FILTERS.granularity);
    setDateRangeOption(DEFAULT_FILTERS.dateRangeOption);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setSelectedClients(DEFAULT_FILTERS.selectedClients);
    setComparePrevious(DEFAULT_FILTERS.comparePrevious);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      let blob;
      let filename;
      const suffix = `${granularity.toLowerCase()}_${startDate}-${endDate}`;
      if (activeReport === 'overall') {
        blob = await exportSummary(granularity, startDate, endDate, selectedClients);
        filename = `overall_summary_${suffix}.csv`;
      } else if (activeReport === 'client') {
        blob = await exportSummaryPerClient(granularity, startDate, endDate, selectedClients);
        filename = `client_summary_${suffix}.csv`;
      } else if (activeReport === 'courier') {
        blob = await exportCourierDispatchSummary(granularity, startDate, endDate, selectedClients);
        filename = `courier_dispatch_${suffix}.csv`;
      } else {
        blob = await exportFulfillmentReport(granularity, startDate, endDate, selectedClients);
        filename = `fulfillment_${suffix}.csv`;
      }
      downloadBlob(blob, filename);
      showSnackbar(`${activeReportMeta.label} exported successfully.`, 'success');
    } catch (error) {
      console.error('Report export error:', error);
      showSnackbar('Failed to export the selected report.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const perClientData = useMemo(() => {
    const grouped = new Map();
    clientSummaryData.forEach((item) => {
      if (!grouped.has(item.clientName)) grouped.set(item.clientName, []);
      const label = granularity === 'Day'
        ? moment(item.date).format('YYYY-MM-DD')
        : granularity === 'Week'
          ? `Week Starting ${moment(item.weekStartDate).format('YYYY-MM-DD')}`
          : moment(`${item.year}-${item.month}-01`).format('YYYY MMM');
      grouped.get(item.clientName).push({
        label,
        ReceivedOrders: Number(item.receivedOrders || 0),
        DispatchedOrders: Number(item.dispatchedOrders || 0),
        CancelledOrders: Number(item.cancelledOrders || 0),
        RemainingInProduction: Math.max(
          Number(item.receivedOrders || 0)
          - Number(item.dispatchedOrders || 0)
          - Number(item.cancelledOrders || 0),
          0
        ),
      });
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([clientName, monthlyData]) => ({ clientName, monthlyData }));
  }, [clientSummaryData, granularity]);

  return (
    <Layout>
      <Box sx={{ p: { xs: 1, md: 1.25 }, minHeight: '100vh', bgcolor: '#f4f7fb' }}>
        <Paper
          variant="outlined"
          sx={{
            px: { xs: 1.5, md: 1.75 },
            py: 0.55,
            mb: 0.5,
            borderRadius: 2.25,
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
            borderLeft: '4px solid',
            borderLeftColor: 'primary.main',
            color: 'text.primary',
            background: 'linear-gradient(100deg, #ffffff 0%, #f7f9fc 100%)',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.75,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              >
                <AssessmentOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                  Orders Reporting
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {lastRefreshed
                  ? `Updated ${moment(lastRefreshed).format('HH:mm:ss')}`
                  : 'Preparing data'}
              </Typography>
              <Chip
                size="small"
                label={`${moment(startDate).format('DD MMM')} – ${moment(endDate).format('DD MMM YYYY')}`}
                variant="outlined"
                sx={{ fontWeight: 800, bgcolor: '#fff' }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1, md: 0.7 },
            mb: 0.5,
            borderRadius: 2.25,
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.045)',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: '135px 145px minmax(210px, 1fr) 125px 125px auto',
              },
              gap: 0.6,
              alignItems: 'center',
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select value={dateRangeOption} label="Date Range" onChange={handleDateRangeOptionChange}>
                {DATE_RANGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={granularity}
              exclusive
              size="small"
              fullWidth
              onChange={(event, value) => value && setGranularity(value)}
            >
              {['Day', 'Week', 'Month'].map((value) => (
                <ToggleButton
                  key={value}
                  value={value}
                  disabled={!availableGranularities.includes(value)}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  {value}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Autocomplete
              multiple
              size="small"
              options={clientOptions}
              value={selectedClients}
              onChange={(event, values) => setSelectedClients(values)}
              limitTags={2}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clients"
                  placeholder={selectedClients.length === 0 ? 'All clients' : ''}
                />
              )}
            />
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(event) => {
                setStartDate(event.target.value);
                setDateRangeOption('custom');
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={endDate}
              onChange={(event) => {
                setEndDate(event.target.value);
                setDateRangeOption('custom');
              }}
              InputLabelProps={{ shrink: true }}
            />
            <Stack
              direction="row"
              spacing={0.35}
              alignItems="center"
              sx={{
                whiteSpace: 'nowrap',
                gridColumn: { xs: '1 / -1', lg: 'auto' },
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
              }}
              title={comparePrevious && previousPeriod
                ? `Comparing ${previousPeriod.startDate} to ${previousPeriod.endDate}`
                : 'Previous-period comparison is off'}
            >
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    checked={comparePrevious}
                    onChange={(event) => setComparePrevious(event.target.checked)}
                  />
                )}
                label="Compare"
                sx={{
                  mr: 0.25,
                  '& .MuiFormControlLabel-label': { fontSize: 11.5 },
                }}
              />
              <Button
                size="small"
                onClick={handleReset}
                disabled={loading}
                sx={{ minWidth: 0, px: 0.65 }}
                title="Reset filters"
              >
                <RestartAltIcon sx={{ fontSize: 17 }} />
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setRefreshKey((value) => value + 1)}
                disabled={loading}
                sx={{ minWidth: 0, px: 0.65 }}
                title="Refresh report"
              >
                <RefreshIcon sx={{ fontSize: 17 }} />
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exporting || loading}
                sx={{ px: 0.85, fontSize: 11.5 }}
              >
                CSV
              </Button>
            </Stack>
          </Box>
        </Paper>

        {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}

        <Box sx={{ mb: 0.5 }}>
          <ReportKpiCards
            current={currentKpis}
            previous={previousKpis}
            showComparison={comparePrevious}
          />
        </Box>

        <Paper variant="outlined" sx={{ p: 0.4, mb: 0.5, borderRadius: 2.25 }}>
          <ToggleButtonGroup
            value={activeReport}
            exclusive
            onChange={(event, value) => value && setActiveReport(value)}
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid !important',
                borderColor: 'divider !important',
                borderRadius: '12px !important',
                m: '0 !important',
              },
            }}
          >
            {REPORTS.map((report) => {
              const Icon = report.icon;
              return (
                <ToggleButton
                  key={report.value}
                  value={report.value}
                  sx={{
                    px: 1,
                    py: 0.55,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    textTransform: 'none',
                    color: '#334155',
                    bgcolor: '#fff',
                    transition: 'background-color 140ms ease, color 140ms ease, border-color 140ms ease',
                    '&:hover': {
                      bgcolor: '#eef4fa',
                      color: '#173a5e',
                      borderColor: '#8fa9c2 !important',
                    },
                    '&&.Mui-selected': {
                      bgcolor: '#234a76',
                      color: '#fff',
                      borderColor: '#234a76 !important',
                      '& .MuiTypography-root': {
                        color: '#fff',
                      },
                      '&:hover': {
                        bgcolor: '#1b3b60',
                        color: '#fff',
                        borderColor: '#1b3b60 !important',
                      },
                    },
                  }}
                >
                  <Icon sx={{ mr: 0.8, fontSize: 19, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>{report.label}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: { xs: 'none', xl: 'block' }, lineHeight: 1.15 }}
                    >
                      {report.description}
                    </Typography>
                  </Box>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: { xs: 1, md: 1.25 }, borderRadius: 2.25, minHeight: 420, overflow: 'hidden' }}
        >
          {loading && ['overall', 'client'].includes(activeReport) ? (
            <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}>
              <Stack alignItems="center" spacing={1}>
                <CircularProgress />
                <Typography color="text.secondary">Loading management report…</Typography>
              </Stack>
            </Box>
          ) : (
            <>
              {activeReport === 'overall' && (
                <OverallSummary summaryData={summaryData} granularity={granularity} />
              )}
              {activeReport === 'client' && (
                <ClientSummary perClientData={perClientData} granularity={granularity} />
              )}
              {activeReport === 'courier' && (
                <CourierDispatchReport
                  granularity={granularity}
                  startDate={startDate}
                  endDate={endDate}
                  selectedClients={selectedClients}
                  refreshKey={refreshKey}
                />
              )}
              {activeReport === 'fulfillment' && (
                <FulfillmentReport
                  granularity={granularity}
                  startDate={startDate}
                  endDate={endDate}
                  selectedClients={selectedClients}
                  refreshKey={refreshKey}
                />
              )}
            </>
          )}
        </Paper>
      </Box>
    </Layout>
  );
};

export default Report;
